export interface DownloadTranslation {
    header: {
        home: string;
        login: string;
    };
    hero: {
        title: string;
        subtitle: string;
    };
    platform: {
        windows: string;
        macos: string;
        detectingOS: string;
    };
    download: {
        dmg: string;
        pkg: string;
        exe: string;
        downloadNow: string;
        downloading: string;
        autoDownload: string;
        version: string;
        fileSize: string;
        releaseDate: string;
        recommended: string;
        installer: string;
    };
    features: {
        title: string;
        items: {
            realtime: {
                title: string;
                description: string;
            };
            translation: {
                title: string;
                description: string;
            };
            video: {
                title: string;
                description: string;
            };
            secure: {
                title: string;
                description: string;
            };
        };
    };
    requirements: {
        title: string;
        windows: {
            title: string;
            os: string;
            processor: string;
            memory: string;
            storage: string;
            network: string;
        };
        macos: {
            title: string;
            os: string;
            processor: string;
            memory: string;
            storage: string;
            network: string;
        };
    };
    installGuide: {
        title: string;
        steps: {
            download: {
                number: string;
                title: string;
                description: string;
            };
            install: {
                number: string;
                title: string;
                description: string;
            };
            start: {
                number: string;
                title: string;
                description: string;
            };
        };
    };
    faq: {
        title: string;
        items: {
            forgotPassword: {
                question: string;
                answer: string;
            };
            webFeatures: {
                question: string;
                answer: string;
            };
            howToSignup: {
                question: string;
                answer: string;
            };
            systemRequirements: {
                question: string;
                answer: string;
            };
            mobileApp: {
                question: string;
                answer: string;
            };
        };
    };
}

export const downloadTranslations: Record<string, DownloadTranslation> = {
    en: {
        header: {
            home: 'Home',
            login: 'Login',
        },
        hero: {
            title: 'Download AivueBiz Desktop App',
            subtitle: 'Experience powerful business communication tools on your desktop',
        },
        platform: {
            windows: 'Windows',
            macos: 'macOS',
            detectingOS: 'Detecting your operating system...',
        },
        download: {
            dmg: 'Download DMG',
            pkg: 'Download PKG',
            exe: 'Download ZIP',
            downloadNow: 'Download Now',
            downloading: 'Downloading...',
            autoDownload: 'Download the version optimized for your operating system.',
            version: 'Version',
            fileSize: 'Size',
            releaseDate: 'Released',
            recommended: 'Recommended',
            installer: 'Installer',
        },
        features: {
            title: 'Why AivueBiz Desktop App?',
            items: {
                realtime: {
                    title: 'Real-time Communication',
                    description: '1:1 and group chat with instant messaging',
                },
                translation: {
                    title: 'Auto Translation',
                    description: 'Real-time translation in 30+ languages',
                },
                video: {
                    title: 'Video Calls',
                    description: 'Crystal clear video and audio quality',
                },
                secure: {
                    title: 'Enterprise Security',
                    description: 'End-to-end encryption for all communications',
                },
            },
        },
        requirements: {
            title: 'System Requirements',
            windows: {
                title: 'Windows Requirements',
                os: 'Windows 10 or later (64-bit)',
                processor: 'Intel Core i3 or equivalent',
                memory: '4GB RAM (8GB recommended)',
                storage: '200MB available space',
                network: 'Broadband internet connection',
            },
            macos: {
                title: 'macOS Requirements',
                os: 'macOS 10.15 (Catalina) or later',
                processor: 'Intel or Apple Silicon processor',
                memory: '4GB RAM (8GB recommended)',
                storage: '200MB available space',
                network: 'Broadband internet connection',
            },
        },
        installGuide: {
            title: 'Simple Installation Guide',
            steps: {
                download: {
                    number: '1',
                    title: 'Download',
                    description: 'Download the version for your OS from above',
                },
                install: {
                    number: '2',
                    title: 'Install',
                    description: 'Run the downloaded file and proceed with installation',
                },
                start: {
                    number: '3',
                    title: 'Start',
                    description: 'Sign up and start global communication',
                },
            },
        },
        faq: {
            title: 'Frequently Asked Questions',
            items: {
                forgotPassword: {
                    question: 'I forgot my password. What should I do?',
                    answer: 'Use the "Forgot Password" feature on the login screen of the desktop app. A 6-digit verification code will be sent to your email. After entering the code, you can set a new password.',
                },
                webFeatures: {
                    question: 'Can I use all features on the web?',
                    answer: 'No. Sign-up, video calls, and screen sharing are only available in the desktop app. Web access is for viewing information only.',
                },
                howToSignup: {
                    question: 'How do I sign up?',
                    answer: 'Sign-up is only available through the desktop app. After downloading the app, click the "Sign Up" button on the first screen to proceed.',
                },
                systemRequirements: {
                    question: 'What if my system doesn\'t meet the requirements?',
                    answer: 'Windows 10 or later, or macOS 10.14 or later is required. If you\'re using an older OS version, please update your OS first.',
                },
                mobileApp: {
                    question: 'Can I use it on Android or iPhone?',
                    answer: 'Android and iPhone apps are being prepared for release in 2026.',
                },
            },
        },
    },
    ko: {
        header: {
            home: '홈',
            login: '로그인',
        },
        hero: {
            title: 'AivueBiz 데스크톱 앱 다운로드',
            subtitle: '강력한 비즈니스 커뮤니케이션 도구를 데스크톱에서 경험하세요',
        },
        platform: {
            windows: 'Windows',
            macos: 'macOS',
            detectingOS: '운영체제를 확인하는 중...',
        },
        download: {
            dmg: 'DMG 다운로드',
            pkg: 'PKG 다운로드',
            exe: 'ZIP 다운로드',
            downloadNow: '지금 다운로드',
            downloading: '다운로드 중...',
            autoDownload: '귀하의 운영체제에 최적화된 버전을 다운로드하세요.',
            version: '버전',
            fileSize: '크기',
            releaseDate: '출시일',
            recommended: '권장',
            installer: '설치 프로그램',
        },
        features: {
            title: '왜 AivueBiz 데스크톱 앱인가요?',
            items: {
                realtime: {
                    title: '실시간 커뮤니케이션',
                    description: '1:1 및 그룹 채팅과 즉각적인 메시징',
                },
                translation: {
                    title: '자동 번역',
                    description: '30개 이상 언어로 실시간 번역',
                },
                video: {
                    title: '영상 통화',
                    description: '선명한 영상과 음성 품질',
                },
                secure: {
                    title: '엔터프라이즈 보안',
                    description: '모든 통신에 대한 종단간 암호화',
                },
            },
        },
        requirements: {
            title: '시스템 요구사항',
            windows: {
                title: 'Windows 요구사항',
                os: 'Windows 10 이상 (64비트)',
                processor: 'Intel Core i3 또는 동급',
                memory: '4GB RAM (8GB 권장)',
                storage: '200MB 가용 공간',
                network: '광대역 인터넷 연결',
            },
            macos: {
                title: 'macOS 요구사항',
                os: 'macOS 10.15 (Catalina) 이상',
                processor: 'Intel 또는 Apple Silicon 프로세서',
                memory: '4GB RAM (8GB 권장)',
                storage: '200MB 가용 공간',
                network: '광대역 인터넷 연결',
            },
        },
        installGuide: {
            title: '간단한 설치 가이드',
            steps: {
                download: {
                    number: '1',
                    title: '다운로드',
                    description: '위에서 OS에 맞는 버전을 다운로드하세요',
                },
                install: {
                    number: '2',
                    title: '설치',
                    description: '다운로드한 파일을 실행하여 설치를 진행하세요',
                },
                start: {
                    number: '3',
                    title: '시작',
                    description: '회원가입 후 글로벌 커뮤니케이션을 시작하세요',
                },
            },
        },
        faq: {
            title: '자주 묻는 질문',
            items: {
                forgotPassword: {
                    question: '비밀번호를 잊어버렸어요. 어떻게 하나요?',
                    answer: '데스크톱 앱의 로그인 화면에서 "비밀번호 찾기" 기능을 이용하세요. 이메일로 6자리 인증 코드가 발송되며, 코드 입력 후 새 비밀번호를 설정할 수 있습니다.',
                },
                webFeatures: {
                    question: '웹에서도 모든 기능을 사용할 수 있나요?',
                    answer: '아니요. 회원가입, 영상통화, 화면공유 등은 데스크톱 앱에서만 가능합니다. 웹은 정보 조회용으로만 제공됩니다.',
                },
                howToSignup: {
                    question: '회원가입은 어떻게 하나요?',
                    answer: '회원가입은 데스크톱 앱에서만 가능합니다. 앱을 다운로드한 후 첫 화면에서 "회원가입" 버튼을 클릭하여 진행하세요.',
                },
                systemRequirements: {
                    question: '시스템 요구사항이 맞지 않으면 어떻게 하나요?',
                    answer: 'Windows 10 이상 또는 macOS 10.14 이상이 필요합니다. 구버전 OS를 사용 중이라면 OS 업데이트를 먼저 진행해주세요.',
                },
                mobileApp: {
                    question: '안드로이드폰 또는 아이폰에서는 사용할 수 없나요?',
                    answer: '안드로이드용 앱과 아이폰용 앱은 2026년 출시를 준비중입니다.',
                },
            },
        },
    },
};

export const getDownloadTranslation = (lang: string): DownloadTranslation => {
    return downloadTranslations[lang] || downloadTranslations['en'];
};

export const detectLanguage = (): string => {
    // Always return English by default for global users
    // Users can switch to Korean if needed
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang) return savedLang;

    // Default to English for global users
    return 'en';
};
